import { useState, useRef, useCallback, useEffect } from 'react';
import { type DocumentPreviewData } from '@/lib/workspace/document-service';
import { entityProjection } from '@/lib/sync/entity-projection';

export type { DocumentPreviewData } from '@/lib/workspace/document-service';

// ── Constants ────────────────────────────────────────────────────────────────

/** Delay before the preview popover appears (ms). */
const HOVER_DELAY_MS = 500;

/** Minimum time the preview stays open after the trigger is left (ms). */
const LINGER_MS = 2000;

// ── Singleton close-others bus ────────────────────────────────────────────────
// When one preview opens, all others must close. Each hook instance registers
// a close callback and calls notifyOpen() when it opens.

type CloseCallback = () => void;
const subscribers = new Set<CloseCallback>();

function subscribe(cb: CloseCallback) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

function notifyOpen(except: CloseCallback) {
  for (const cb of subscribers) {
    if (cb !== except) cb();
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages hover-triggered entity preview fetching (documents and canvases).
 *
 * The popover appears after `HOVER_DELAY_MS` of hovering the trigger.
 * When the mouse leaves the trigger, the popover lingers for `LINGER_MS`
 * so the user can move their mouse to the card. While the mouse is over
 * the card, it stays open indefinitely.
 *
 * Only one preview can be open at a time — opening a new one closes any other.
 */
export function useEntityPreview(documentId: string) {
  const [isOpen, setIsOpen] = useState(false);
  const [previewData, setPreviewData] = useState<DocumentPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const enterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOverCardRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (enterTimerRef.current) { clearTimeout(enterTimerRef.current); enterTimerRef.current = null; }
    if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; }
  }, []);

  const close = useCallback(() => {
    clearTimers();
    isOverCardRef.current = false;
    setIsOpen(false);
  }, [clearTimers]);

  // Register this instance with the singleton bus so other previews can close it.
  useEffect(() => {
    const unsubscribe = subscribe(close);
    return () => { unsubscribe(); };
  }, [close]);

  // Called when mouse enters the trigger row
  const onMouseEnter = useCallback(() => {
    // Cancel any pending dismiss
    if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; }

    // If already open, keep it open
    if (isOpen) return;

    // Start the open delay
    if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
    enterTimerRef.current = setTimeout(async () => {
      enterTimerRef.current = null;
      notifyOpen(close);
      setIsOpen(true);
      setLoading(true);

      try {
        // Build preview from workspace store data (no adapter fetch needed)
        const doc = entityProjection.getSnapshot().docs.find((d) => d.id === documentId);
        if (doc) {
          setPreviewData({
            id: doc.id,
            title: doc.title || 'Untitled',
            preview: '',
            wordCount: 0,
            tags: [...(doc.tags ?? [])],
            updatedAt: doc.updatedAt,
            kind: doc.kind,
            previewMarkup: doc.previewMarkup,
          });
        } else {
          setPreviewData(null);
        }
      } catch (err) {
        console.error('[useEntityPreview] Failed to fetch preview:', err);
        setPreviewData(null);
      } finally {
        setLoading(false);
      }
    }, HOVER_DELAY_MS);
  }, [close, documentId, isOpen]);

  // Called when mouse leaves the trigger row
  const onMouseLeave = useCallback(() => {
    // Cancel the open delay if it hasn't fired yet
    if (enterTimerRef.current) { clearTimeout(enterTimerRef.current); enterTimerRef.current = null; }

    // If not open, nothing to dismiss
    if (!isOpen) return;

    // Start the linger timer — gives user time to reach the card
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => {
      dismissTimerRef.current = null;
      // Only close if the mouse isn't over the preview card
      if (!isOverCardRef.current) {
        setIsOpen(false);
      }
    }, LINGER_MS);
  }, [isOpen]);

  // Called when mouse enters the preview card
  const onCardMouseEnter = useCallback(() => {
    isOverCardRef.current = true;
    // Cancel any pending dismiss
    if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; }
  }, []);

  // Called when mouse leaves the preview card
  const onCardMouseLeave = useCallback(() => {
    isOverCardRef.current = false;
    setIsOpen(false);
  }, []);

  return {
    onMouseEnter,
    onMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
    previewData,
    isOpen,
    loading,
    close,
  };
}

export { HOVER_DELAY_MS, LINGER_MS };
